import { useState, useEffect, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { verifyChat } from "@root/store/thunks/chatThunks";
import {
  addInThreadList,
  updateThreadChatById,
} from "@root/store/slices/entities/thread";
import { getThreadList } from "@root/store/selectors/entities/thread";
import { generateRandomId } from "@root/utils/dateHelper";
import { ROLES, CHAT_RESPONSE_STREAM_TIME } from "@root/constants";
import { safeJsonParse } from "@root/utils";
import { parseChatAnswer } from "@root/utils/chatHelper";
import { useTranslation } from "react-i18next";
import { useMainChatContext } from "@root/context/kps-chat-context";
// Global shared state for verifying - all instances check this
// This prevents any SystemChat instance from calling fetchChat when verify is in progress
let globalIsVerifying = false;

interface UseVerifyChatOptions {
  onSuccess?: (data: any) => void;
  onError?: (error: any) => void;
}

interface VerifyChatPayload {
  query: string;
  threadId?: string | number;
  chatId?: string | number;
  chat_history?: any[];
  law_generated_answer?: string;
  law_chat_id?: string | number;
}

// Export function to check global verifying state (used by SystemChat instances)
export const getGlobalIsVerifying = () => globalIsVerifying;

export const useVerifyChat = (options: UseVerifyChatOptions = {}) => {
  const { onSuccess, onError } = options;
  const dispatch = useDispatch();
  const threadList: any = useSelector(getThreadList);
  const [verifyResponse, setVerifyResponse] = useState<any>(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const { setIsPollingVerifyChat, isPollingVerifyChat } = useMainChatContext();
  const verifyChatIdRef = useRef<string | null>(null);
  const verifyChatApiChatIdRef = useRef<string | number | null>(null);
  const isVerifyingRef = useRef<boolean>(false);
  const threadListRef = useRef<any>(threadList);
  const { t } = useTranslation("common");

  // Keep threadListRef in sync with threadList to avoid stale closure issues
  useEffect(() => {
    threadListRef.current = threadList;
  }, [threadList]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      // Only reset global state if this instance was verifying
      if (isVerifyingRef.current) {
        globalIsVerifying = false;
        isVerifyingRef.current = false;
        setIsPollingVerifyChat(false);
      }
    };
  }, []);

  // Initialize verify chat items in thread list (called on first response)
  const initializeVerifyChatItems = useCallback(() => {
    // Use ref to get the latest threadList to avoid stale closure issues
    const currentThreadList = threadListRef.current;
    const lastChat = currentThreadList?.[currentThreadList?.length - 1];
    console.log("lastchat: ", lastChat);
    if (!lastChat || verifyChatIdRef.current) return; // Already initialized

    // Generate a unique ID for the verify chat
    const verifyChatId = generateRandomId("verify-chat");
    verifyChatIdRef.current = verifyChatId;

    const verifyText = t("systemChat.verifyButton");
    const questionText =
      lastChat?.input ||
      lastChat?.meta?.question ||
      lastChat?.meta?.c?.question ||
      "";
    const verifyQuestion = `${verifyText}: ${questionText}`;

    // Create verify chat items with empty/initial answer
    const verifyChatItems = [
      {
        id: verifyChatId,
        role: ROLES.USER,
        input: verifyQuestion,
        threadId: lastChat?.threadId,
        chatId: lastChat?.chatId || lastChat?.id,
        isVerify: true,
        meta: {
          c: {
            question: verifyQuestion,
            isVerify: true,
          },
        },
      },
      {
        id: verifyChatId,
        role: ROLES.ASSISTANT,
        output: "", // Start with empty output for streaming
        threadId: lastChat?.threadId,
        chatId: lastChat?.chatId || lastChat?.id,
        isVerify: true,
        meta: {
          c: {
            isVerify: true,
          },
          answer: "",
          sources: [],
          follow_up: [],
        },
      },
    ];

    // Add to thread list - this is synchronous, items will be in store immediately
    dispatch(addInThreadList(verifyChatItems as any));
  }, [dispatch, t]);

  // Update verify chat response incrementally (called during polling)
  const updateVerifyChatResponse = useCallback(
    (data: any) => {
      if (!data || !verifyChatIdRef.current) return;

      // Parse the answer using the reusable utility function
      const answer = parseChatAnswer(data);

      // Update the assistant message with new answer
      dispatch(
        updateThreadChatById({
          id: verifyChatIdRef.current,
          payload: {
            output: answer,
            meta: {
              c: {
                ...data,
                isVerify: true,
              },
              answer: answer,
              sources: safeJsonParse(data?.answer)?.sources || [],
              follow_up: safeJsonParse(data?.answer)?.follow_up || [],
            },
          },
          merge: true,
        } as any)
      );
    },
    [dispatch]
  );

  // Handle verify chat API call
  const handleVerifyChat = useCallback(
    (payload: VerifyChatPayload) => {
      if (isVerifyingRef.current || globalIsVerifying) return; // Prevent multiple simultaneous calls

      // Set global and local state
      globalIsVerifying = true;
      isVerifyingRef.current = true;
      setIsVerifying(true);
      setIsPollingVerifyChat(false); // Reset polling state for new verify chat
      setVerifyResponse(null);
      verifyChatIdRef.current = null; // Reset for new verify chat
      verifyChatApiChatIdRef.current = null; // Reset for new verify chat

      const verifyPayload = {
        payload,
        onSuccess: (data: any) => {
          setVerifyResponse(data);
          const status = data?.status;

          // Extract chatId from first response and store it
          if (verifyChatApiChatIdRef.current === null && data?.chatId) {
            verifyChatApiChatIdRef.current = data.chatId;
          }

          // Initialize chat items on first response (if not already initialized)
          if (!verifyChatIdRef.current) {
            initializeVerifyChatItems();
            // After initialization, verifyChatIdRef.current is set and items are in store
            // Now it's safe to update the response
          }

          // Update the response incrementally (only if initialization succeeded)
          if (verifyChatIdRef.current) {
            updateVerifyChatResponse(data);
          }

          // If status >= 4, response is completed
          if (status >= 4) {
            globalIsVerifying = false;
            isVerifyingRef.current = false;
            setIsVerifying(false);
            setIsPollingVerifyChat(false);
            if (onSuccess) {
              onSuccess(data);
            }
            // Stop polling if active
            if (pollingIntervalRef.current) {
              clearInterval(pollingIntervalRef.current);
              pollingIntervalRef.current = null;
            }
          } else {
            // Start polling using centralized interval constant
            if (!pollingIntervalRef.current) {
              setIsPollingVerifyChat(true);
              pollingIntervalRef.current = setInterval(() => {
                // Include chatId in polling payload if available
                const pollingPayload = verifyChatApiChatIdRef.current
                  ? { ...payload, chatId: verifyChatApiChatIdRef.current }
                  : payload;

                dispatch(
                  verifyChat({
                    payload: pollingPayload,
                    onSuccess: (polledData: any) => {
                      const polledStatus = polledData?.status;
                      setVerifyResponse(polledData);

                      // Extract chatId from polling response if not already stored
                      if (
                        verifyChatApiChatIdRef.current === null &&
                        polledData?.chatId
                      ) {
                        verifyChatApiChatIdRef.current = polledData.chatId;
                      }

                      // Update the response incrementally during polling
                      updateVerifyChatResponse(polledData);

                      // If status >= 4, stop polling
                      if (polledStatus >= 4) {
                        globalIsVerifying = false;
                        isVerifyingRef.current = false;
                        setIsVerifying(false);
                        setIsPollingVerifyChat(false);
                        if (onSuccess) {
                          onSuccess(polledData);
                        }
                        if (pollingIntervalRef.current) {
                          clearInterval(pollingIntervalRef.current);
                          pollingIntervalRef.current = null;
                        }
                      }
                    },
                    onError: (error: any) => {
                      globalIsVerifying = false;
                      isVerifyingRef.current = false;
                      setIsVerifying(false);
                      setIsPollingVerifyChat(false);
                      if (pollingIntervalRef.current) {
                        clearInterval(pollingIntervalRef.current);
                        pollingIntervalRef.current = null;
                      }
                      if (onError) {
                        onError(error);
                      }
                    },
                  })
                );
              }, CHAT_RESPONSE_STREAM_TIME.VERIFY_CHAT_POLLING_INTERVAL);
            }
          }
        },
        onError: (error: any) => {
          globalIsVerifying = false;
          isVerifyingRef.current = false;
          setIsVerifying(false);
          setIsPollingVerifyChat(false);
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
          if (onError) {
            onError(error);
          }
        },
      };

      dispatch(verifyChat(verifyPayload));
    },
    [
      dispatch,
      initializeVerifyChatItems,
      updateVerifyChatResponse,
      onSuccess,
      onError,
    ]
  );

  return {
    handleVerifyChat,
    verifyResponse,
    isVerifying,
    isPollingVerifyChat,
  };
};

export default useVerifyChat;
